import type { WsAppSyncEvent } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("appsync");

export type QueueKey = string;

export interface EventQueue {
  accountId: string;
  workspaceId: string;
  events: WsAppSyncEvent[];
}

export const queues = new Map<QueueKey, EventQueue>();

export function getQueueKey(accountId: string, workspaceId: string): QueueKey {
  return `${accountId}:${workspaceId || ""}`;
}

export function deduplicateById<T extends { id: string }>(arr: T[]): T[] {
  const map = new Map<string, T>();
  for (const item of arr) {
    map.set(item.id, item);
  }
  return Array.from(map.values());
}

export function deDuplicateByworkspaceMembershipId<T extends { workspaceMembershipId?: string }>(arr: T[]): T[] {
  const map = new Map<string, T>();
  for (const item of arr) {
    map.set(item.workspaceMembershipId ?? "unknown", item);
  }
  return Array.from(map.values());
}

export const deduplicateByKeyValue = <T>(arr: T[], keyOf: (item: T) => string): T[] => {
  const map = new Map<string, T>();
  for (const item of arr) {
    map.set(keyOf(item), item);
  }
  const deduped = Array.from(map.values()).filter((i) => i);
  if (arr.length !== deduped.length) {
    log.debug("Items deduplicated", arr.length - deduped.length);
  }
  return deduped;
};

export function mergeEvents(events: WsAppSyncEvent[]): WsAppSyncEvent {
  const merged: WsAppSyncEvent = {
    accounts: [],
    asyncEchos: [],
    audioEncodings: [],
    broadcastActions: [],
    broadcastRecipients: [],
    callRecords: [],
    commandAliases: [],
    directWorkspaceInvitations: [],
    displayArtifacts: [],
    events: [],
    feedGroupMemberships: [],
    feedGroups: [],
    feedPreferences: [],
    feeds: [],
    files: [],
    items: [],
    links: [],
    membershipAvailabilities: [],
    permissions: [],
    scheduleTriggers: [],
    templates: [],
    transcriptions: [],
    workflowItems: [],
    workspaceMemberships: [],
    workspaceMembershipTags: [],
    workspaceTags: [],
    workspaces: [],
    tmsEventStreams: [],
    assignmentIntervals: [],
    acknowledgements: [],
    tasks: [],
  };

  for (const ev of events) {
    if (ev.accounts) merged.accounts.push(...ev.accounts);
    if (ev.workspaces) merged.workspaces.push(...ev.workspaces);
    if (ev.workspaceMemberships) merged.workspaceMemberships.push(...ev.workspaceMemberships);
    if (ev.directWorkspaceInvitations) merged.directWorkspaceInvitations.push(...ev.directWorkspaceInvitations);
    if (ev.feeds) merged.feeds.push(...ev.feeds);
    if (ev.feedGroups) merged.feedGroups.push(...ev.feedGroups);
    if (ev.feedGroupMemberships) merged.feedGroupMemberships.push(...ev.feedGroupMemberships);
    if (ev.permissions) merged.permissions.push(...ev.permissions);
    if (ev.audioEncodings) merged.audioEncodings.push(...ev.audioEncodings);
    if (ev.displayArtifacts) merged.displayArtifacts.push(...ev.displayArtifacts);
    if (ev.files) merged.files.push(...ev.files);
    if (ev.links) merged.links.push(...ev.links);
    if (ev.transcriptions) merged.transcriptions.push(...ev.transcriptions);
    if (ev.commandAliases) merged.commandAliases.push(...ev.commandAliases);
    if (ev.workflowItems) merged.workflowItems.push(...ev.workflowItems);
    if (ev.scheduleTriggers) merged.scheduleTriggers.push(...ev.scheduleTriggers);
    if (ev.broadcastActions) merged.broadcastActions.push(...ev.broadcastActions);
    if (ev.broadcastRecipients) merged.broadcastRecipients.push(...ev.broadcastRecipients);
    if (ev.templates) merged.templates.push(...ev.templates);
    if (ev.events) merged.events.push(...ev.events);
    if (ev.items) merged.items.push(...ev.items);
    if (ev.feedPreferences) merged.feedPreferences.push(...ev.feedPreferences);
    if (ev.membershipAvailabilities) merged.membershipAvailabilities.push(...ev.membershipAvailabilities);
    if (ev.asyncEchos) merged.asyncEchos.push(...ev.asyncEchos);
    if (ev.workspaceMembershipTags) merged.workspaceMembershipTags.push(...ev.workspaceMembershipTags);
    if (ev.workspaceTags) merged.workspaceTags.push(...ev.workspaceTags);
    if (ev.callConfiguration) merged.callConfiguration = ev.callConfiguration;
    if (ev.callRecords) merged.callRecords.push(...ev.callRecords);
    // The hints carry no rows, only what to re-walk: a merged batch must keep every one of them.
    if (ev.tmsEventStreams) merged.tmsEventStreams?.push(...ev.tmsEventStreams);
    if (ev.assignmentIntervals) merged.assignmentIntervals?.push(...ev.assignmentIntervals);
    if (ev.acknowledgements) merged.acknowledgements?.push(...ev.acknowledgements);
    if (ev.tasks) merged.tasks?.push(...ev.tasks);
  }
  merged.transcriptions = deduplicateById(merged.transcriptions);
  merged.audioEncodings = deduplicateById(merged.audioEncodings);
  merged.callRecords = deduplicateById(merged.callRecords);
  merged.events = deduplicateById(merged.events);
  merged.items = deduplicateById(merged.items);
  merged.feedPreferences = deduplicateById(merged.feedPreferences);
  merged.permissions = deduplicateById(merged.permissions);

  merged.membershipAvailabilities = deDuplicateByworkspaceMembershipId(merged.membershipAvailabilities);
  merged.workspaceMembershipTags = deduplicateByKeyValue(
    merged.workspaceMembershipTags,
    (t) => `${t.workspaceMembershipId}:${t.tagId}:${t.tagValue}`,
  );
  merged.workspaceTags = deduplicateById(merged.workspaceTags);
  merged.tmsEventStreams = deduplicateByKeyValue(merged.tmsEventStreams ?? [], (hint) => hint.stream);
  merged.assignmentIntervals = deduplicateByKeyValue(
    merged.assignmentIntervals ?? [],
    (hint) => hint.workspaceIntegrationId,
  );
  merged.acknowledgements = deduplicateByKeyValue(merged.acknowledgements ?? [], (hint) => hint.subject);
  merged.tasks = deduplicateByKeyValue(merged.tasks ?? [], (hint) => hint.log);

  return merged;
}
